import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHttpQuery } from '@/api';
import { Store } from '@/redux';
import { CoinPile } from '@/shared/components/reward/coin-pile';
import { DailyDeal } from '@/shared/components/reward/daily-deal-card';
import {
  CoinWallet,
  CoinWalletTransaction,
  describeTransaction,
  formatActivityTime,
  formatCoins,
  formatSignedCoins,
} from '@/shared/components/reward/coin-wallet';
import {
  useCoinWallet,
  useNow,
} from '@/shared/components/reward/use-coin-wallet';
import '@/shared/components/reward/coin-wallet.css';
import { API_URLS } from '../consts';

interface ProgressionData {
  xp: number;
  totalXPEarned: number;
  gamesPlayed: number;
  wins: number;
  losses: number;
  levelName: string;
  levelEmoji: string;
  currentLevelMinXP: number;
  nextLevelMinXP: number;
}

const LEVEL_ORDER = [
  'Beginner',
  'Rookie',
  'Intermediate',
  'Advanced',
  'Pro',
  'Expert',
  'King',
];

function nextLevelName(current: string): string {
  const idx = LEVEL_ORDER.findIndex(l => l.startsWith(current));
  if (idx === -1 || idx >= LEVEL_ORDER.length - 1) return 'max';
  return LEVEL_ORDER[idx + 1];
}

// Rules mirrored from the server (operations/coins-operations.go,
// progression-operations.go). Entry fee and daily deal come from the API.
const MATCH_FINISH_BONUS = 50;
const CHAT_FEE = 100;
const CHAT_MESSAGES_PER_FEE = 100;
const INVITE_REWARD = 50;

/** "Enough for 3 account matches" / "120 more for your next match". */
export const affordabilityNote = (balance: number, entryFee: number) => {
  if (entryFee <= 0) return null;
  const matches = Math.floor(balance / entryFee);
  if (matches >= 1) {
    return {
      strong: `${formatCoins(matches)} ${matches === 1 ? 'match' : 'matches'}`,
      text: 'Enough for',
      after: `at ${formatCoins(entryFee)} coins each.`,
    };
  }
  return {
    strong: `${formatCoins(entryFee - balance)} more coins`,
    text: 'You need',
    after: 'for your next account match.',
  };
};

export const CoinsInfoPage: FC = () => {
  const [tab, setTab] = useState<'balance' | 'daily' | 'activity' | 'guide' | 'rank'>('balance');
  const [activityPage, setActivityPage] = useState(0);
  const userId = useSelector((s: Store) => s.userReducer.id);
  const w = useCoinWallet();
  const clock = useNow(!!w.wallet, 60_000);

  const { data: prog } = useHttpQuery<ProgressionData>(
    'user-vault-progression-' + userId,
    API_URLS.USER_PROGRESSION,
    'GET',
    undefined,
    { enabled: w.enabled }
  );

  if (!w.enabled) {
    return (
      <main className="cw-page">
        <div className="cw-page__inner cw-state">
          <CoinPile size="xl" />
          <h1>Coins live in your account</h1>
          <p>
            Sign in with an account to keep a wallet. Guest players never need
            coins.
          </p>
        </div>
      </main>
    );
  }

  if (!w.wallet) {
    return (
      <main className="cw-page" aria-busy={!w.isError}>
        <div className="cw-page__inner cw-state">
          <CoinPile size="xl" animated={!w.isError} />
          {w.isError ? (
            <>
              <h1>We couldn&apos;t open your wallet</h1>
              <p>Your coins are safe — this is just a connection problem.</p>
              <button
                type="button"
                className="st-btn st-btn--primary"
                onClick={w.refetch}
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <h1>Counting your coins…</h1>
              <p className="st-sr-only">Loading wallet</p>
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="cw-page">
      <div className="cw-page__inner">
        <nav className="cw-screen-tabs" aria-label="Wallet sections">
          {([['balance', 'Wallet'], ['daily', 'Daily deal'], ['activity', 'Activity'], ['guide', 'Guide'], ['rank', 'Rank']] as const).map(([value, label]) => (
            <button key={value} type="button" aria-pressed={tab === value} onClick={() => setTab(value)}>{label}</button>
          ))}
        </nav>
        <div className="cw-screen-panel" key={tab}>
          {tab === 'balance' && <>
            <WalletHero wallet={w.wallet} />
            <Totals wallet={w.wallet} />
            <button type="button" className="st-btn st-btn--primary st-btn--block" onClick={() => setTab('daily')}>Open daily deal</button>
            <p className="cw-panel__hint">Account matches cost {formatCoins(w.wallet.entryFee)} coins. Guest play is always free.</p>
          </>}
          {tab === 'daily' && <DailyDeal compact />}
          {tab === 'guide' && <CoinRules wallet={w.wallet} />}
          {tab === 'activity' && <>
            <Activity transactions={w.wallet.transactions.slice(activityPage * 3, activityPage * 3 + 3)} now={Math.max(clock, w.now)} />
            <nav className="cw-pagination" aria-label="Activity pages">
              <button type="button" disabled={activityPage === 0} onClick={() => setActivityPage(p => p - 1)}>Previous</button>
              <span>Page {activityPage + 1} of {Math.max(1, Math.ceil(w.wallet.transactions.length / 3))}</span>
              <button type="button" disabled={(activityPage + 1) * 3 >= w.wallet.transactions.length} onClick={() => setActivityPage(p => p + 1)}>Next</button>
            </nav>
          </>}
          {tab === 'rank' && (prog ? <Progression prog={prog} /> : <p role="status">Your rank will appear after your account stats load.</p>)}
        </div>
      </div>
    </main>
  );
};

const WalletHero: FC<{ wallet: CoinWallet }> = ({ wallet }) => {
  const note = affordabilityNote(wallet.balance, wallet.entryFee);
  return (
    <header className="cw-hero">
      <CoinPile size="hero" animated label="Gold coins" />
      <div>
        <p className="cw-hero__eyebrow">Your wallet</p>
        <p className="cw-hero__balance" aria-live="polite">
          {formatCoins(wallet.balance)}
          <span>coins</span>
        </p>
        {note && (
          <p className="cw-hero__note">
            {note.text} <strong>{note.strong}</strong> {note.after}
          </p>
        )}
      </div>
    </header>
  );
};

const Totals: FC<{ wallet: CoinWallet }> = ({ wallet }) => {
  const net = wallet.earned - wallet.spent;
  return (
    <section className="cw-panel" aria-labelledby="cw-totals">
      <h2 className="cw-panel__title" id="cw-totals">
        Lifetime
      </h2>
      <dl className="cw-totals">
        <div className="cw-total">
          <dt>Earned</dt>
          <dd className="cw-pos">+{formatCoins(wallet.earned)}</dd>
        </div>
        <div className="cw-total">
          <dt>Spent</dt>
          <dd className="cw-neg">−{formatCoins(wallet.spent)}</dd>
        </div>
        <div className="cw-total">
          <dt>Net</dt>
          <dd className={net >= 0 ? 'cw-pos' : 'cw-neg'}>
            {net >= 0 ? '+' : '−'}
            {formatCoins(Math.abs(net))}
          </dd>
        </div>
      </dl>
    </section>
  );
};

const CoinRules: FC<{ wallet: CoinWallet }> = ({ wallet }) => (
  <section className="cw-panel" aria-labelledby="cw-rules">
    <h2 className="cw-panel__title" id="cw-rules">
      How coins move
    </h2>
    <div className="cw-rules">
      <div>
        <h3>Earn</h3>
        <ul>
          <li>
            <span>
              Daily deal
              <small>Every 12 hours</small>
            </span>
            <b>+{formatCoins(wallet.rewardAmount)}</b>
          </li>
          <li>
            <span>
              Finish a match
              <small>
                Everyone but last place splits the pot, plus a finish bonus
              </small>
            </span>
            <b>+{MATCH_FINISH_BONUS}</b>
          </li>
          <li>
            <span>
              Invite a friend
              <small>You both get it after their first game</small>
            </span>
            <b>+{INVITE_REWARD}</b>
          </li>
        </ul>
      </div>
      <div>
        <h3>Spend</h3>
        <ul>
          <li>
            <span>
              Account match entry
              <small>Goes into the pot</small>
            </span>
            <b>−{formatCoins(wallet.entryFee)}</b>
          </li>
          <li>
            <span>
              Unlock a chat
              <small>{CHAT_MESSAGES_PER_FEE} messages</small>
            </span>
            <b>−{CHAT_FEE}</b>
          </li>
        </ul>
      </div>
    </div>
    <p className="cw-panel__hint">
      Coins are for fun only — they can&apos;t be bought, sold or cashed out.
      Guest players never need coins.
    </p>
  </section>
);

const Activity: FC<{
  transactions: CoinWalletTransaction[];
  now: number;
}> = ({ transactions, now }) => (
  <section className="cw-panel" aria-labelledby="cw-activity">
    <h2 className="cw-panel__title" id="cw-activity">
      Recent activity
    </h2>
    {transactions.length === 0 ? (
      <p className="cw-empty">
        No coin activity yet. Claim your daily deal or play a match to start
        your ledger.
      </p>
    ) : (
      <ul className="cw-activity">
        {transactions.map(tx => {
          const { label } = describeTransaction(tx);
          const earned = tx.type !== 'spent';
          return (
            <li key={tx.id}>
              <span
                className={`cw-activity__icon ${earned ? 'cw-activity__icon--earned' : ''}`}
                aria-hidden="true"
              >
                {earned ? '+' : '−'}
              </span>
              <span>
                <p className="cw-activity__label">{label}</p>
                <p className="cw-activity__time">
                  <time dateTime={tx.createdAt}>
                    {formatActivityTime(tx.createdAt, now)}
                  </time>
                </p>
              </span>
              <span
                className={`cw-activity__amount ${earned ? 'cw-pos' : 'cw-neg'}`}
              >
                {formatSignedCoins(tx.amount, tx.type)}
              </span>
            </li>
          );
        })}
      </ul>
    )}
    <p className="cw-panel__hint">
      Shows up to your latest 20 entries. Some older bonuses (like past wheel
      prizes) were added before this ledger existed, so lifetime totals can be
      higher than the list.
    </p>
  </section>
);

const Progression: FC<{ prog: ProgressionData }> = ({ prog }) => {
  const isMax = prog.nextLevelMinXP === -1;
  const span = prog.nextLevelMinXP - prog.currentLevelMinXP;
  const pct = isMax
    ? 100
    : span > 0
      ? Math.max(
          0,
          Math.min(
            100,
            Math.round(((prog.xp - prog.currentLevelMinXP) / span) * 100)
          )
        )
      : 0;
  const winRate =
    prog.gamesPlayed > 0 ? Math.round((prog.wins / prog.gamesPlayed) * 100) : 0;
  return (
    <section className="cw-panel" aria-labelledby="cw-rank">
      <div className="cw-rank">
        <div>
          <p className="cw-hero__eyebrow">Rank</p>
          <p className="cw-rank__name" id="cw-rank">
            <span aria-hidden="true">{prog.levelEmoji}</span> {prog.levelName}
          </p>
        </div>
        <p className="cw-rank__xp">
          {isMax
            ? `${formatCoins(prog.xp)} XP · max level`
            : `${formatCoins(prog.xp)} / ${formatCoins(prog.nextLevelMinXP)} XP`}
          {!isMax && (
            <>
              <br />
              {formatCoins(prog.nextLevelMinXP - prog.xp)} XP to{' '}
              {nextLevelName(prog.levelName)}
            </>
          )}
        </p>
      </div>
      <div
        className="cw-progress"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label="Progress to next rank"
      >
        <span style={{ width: `${pct}%` }} />
      </div>
      <dl className="cw-stats">
        <div className="cw-total">
          <dt>Games</dt>
          <dd>{formatCoins(prog.gamesPlayed)}</dd>
        </div>
        <div className="cw-total">
          <dt>Wins</dt>
          <dd className="cw-pos">{formatCoins(prog.wins)}</dd>
        </div>
        <div className="cw-total">
          <dt>Losses</dt>
          <dd>{formatCoins(prog.losses)}</dd>
        </div>
        <div className="cw-total">
          <dt>Win rate</dt>
          <dd>{winRate}%</dd>
        </div>
      </dl>
      <p className="cw-panel__hint">
        {formatCoins(prog.totalXPEarned)} XP earned all-time.
      </p>
    </section>
  );
};
